export type WakeLockResult =
  | 'unsupported'
  | 'granted'
  | 'rejected'
  | 'released'
  | 'reacquired';

export interface WakeSentinel {
  readonly released: boolean;
  addEventListener(type: 'release', listener: () => void): void;
  removeEventListener(type: 'release', listener: () => void): void;
  release(): Promise<void>;
}

interface WakeLockDependencies {
  request?: () => Promise<WakeSentinel>;
  isVisible: () => boolean;
  onResult: (result: WakeLockResult, error?: string) => void;
}

/**
 * A wake lock belongs to a visible document, not to the sit in the abstract. Android and
 * WebKit both release the sentinel when the page is hidden, so retaining the original
 * promise or sentinel cannot protect the screen after return.
 */
export class WakeLockController {
  private active = false;
  private enabled = false;
  private sentinel: WakeSentinel | null = null;
  private pending: Promise<void> | null = null;
  private hasHeldLock = false;

  constructor(private readonly deps: WakeLockDependencies) {}

  start(enabled: boolean): Promise<void> {
    this.active = true;
    this.enabled = enabled;
    return this.sync();
  }

  visibilityChanged(): Promise<void> {
    return this.sync();
  }

  stop(): void {
    this.active = false;
    this.enabled = false;
    const sentinel = this.sentinel;
    this.sentinel = null;
    if (!sentinel) return;
    sentinel.removeEventListener('release', this.onRelease);
    // Record ownership ending before the caller closes its trace. `release()` resolves on
    // a later task, which made this final state disappear at every normal sit end.
    this.deps.onResult('released');
    void sentinel.release().catch(() => undefined);
  }

  private sync(): Promise<void> {
    if (!this.active || !this.enabled || !this.deps.isVisible() || this.sentinel) {
      return Promise.resolve();
    }
    if (this.pending) return this.pending;
    if (!this.deps.request) {
      this.deps.onResult('unsupported');
      return Promise.resolve();
    }

    const reacquiring = this.hasHeldLock;
    const request = this.deps.request()
      .then((sentinel) => {
        // The page can become hidden or the sit can end while permission UI is open.
        // Keeping that late sentinel would make ownership cross the sit boundary.
        if (!this.active || !this.enabled || !this.deps.isVisible()) {
          void sentinel.release();
          return;
        }
        this.sentinel = sentinel;
        sentinel.addEventListener('release', this.onRelease);
        this.hasHeldLock = true;
        this.deps.onResult(reacquiring ? 'reacquired' : 'granted');
      }, (error: unknown) => {
        this.deps.onResult(
          'rejected',
          error instanceof Error ? error.message : String(error),
        );
      })
      .finally(() => {
        if (this.pending === request) this.pending = null;
      });
    this.pending = request;
    return request;
  }

  private readonly onRelease = () => {
    const sentinel = this.sentinel;
    if (!sentinel) return;
    sentinel.removeEventListener('release', this.onRelease);
    this.sentinel = null;
    this.deps.onResult('released');
    // A policy revocation can happen while visible. Retrying from its own release event
    // can become a battery-burning rejection loop, so reacquisition waits for the next
    // visible lifecycle transition.
  };
}
